import { debounce, isInteger, isNil, isString } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";

import KeyConstants from "../../constants/keyConstants";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions from "../../constants/styles/dimensions";
import timeoutRates from "../../constants/timeoutRates";
import { calcTextSize } from "../../utils/calcTextSize";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import localUndoUtils from "../../utils/localUndoUtils";
import { withCharacterContext } from "../character/CharacterContext";
// import { findAncestorOfClass } from "../../utils/hasAncestorOfClass";
// import { hasNoSiblings } from "../../utils/domUtils";
import { withPortalChildContext } from "../portal/PortalChildContext";

const trimValueBeforeCommit = value => (isString(value) ? value.trim() : value);

class FormTextField extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = { initialValue: "", value: "" };
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSendChangeToProps = this.onSendChangeToProps.bind(this);
    this.applyPresentMoment = this.applyPresentMoment.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onInitialSlidePortalTransitionEnd = this.onInitialSlidePortalTransitionEnd.bind(this);
    this.getSelectionRange = this.getSelectionRange.bind(this);
    this.clearUndoStack = this.clearUndoStack.bind(this);
    this.updateUndoStack = this.updateUndoStack.bind(this);
    this.updateUndoSelection = this.updateUndoSelection.bind(this);
    this.undoChange = this.undoChange.bind(this);
    this.redoChange = this.redoChange.bind(this);
    this.debouncedUpdateUndoStack = debounce(this.updateUndoStack, timeoutRates.UNDO_TEXT_TIMEOUT);

    this.validate = this.validate.bind(this);

    this.focus = this.focus.bind(this);

    this.clearUndoStack(this.state.value);

    this._domNode = null;
    this._uncommittedChange = false;
    this._isFocused = false;
  }

  componentWillMount() {
    const value = !isNil(this.props.value) ? this.props.value : "";
    this.setState({ value: value, initialValue: value });
    this._uncommittedChange = false;
  }

  componentDidMount() {
    const autoFocus = this.props.autoFocus || (this.props.autoFocusOnEmpty && !this.state.value);
    if (autoFocus) {
      // If we have a PortalContext, then we want the portal to be in charge of when we focus
      if (this.props.portalChildContext && this.props.portalChildContext.registerTransitionEndCallback) {
        this.props.portalChildContext.registerTransitionEndCallback(this.onInitialSlidePortalTransitionEnd, true);
      } else {
        this.focus();
      }
    }
    // Initial the size if necessary
    if (this.props.multiline && this.props.autoExpandHeight && this._domNode) {
      const newStyle = this.getAutoExpandSize();
      this._domNode.style.height = `${newStyle.height}px`;
    }
  }

  componentWillReceiveProps(nextProps) {
    // If there are changes to commit, we need to commit them before the new props
    // roll in...
    if (this._uncommittedChange) {
      this.commitChange();
    }
    // Work out what value we will be using next
    const value = !isNil(nextProps.value) ? nextProps.value : "";

    // Normally, we want to clear our undo stack as soon as we get a value from outside
    // However, if we are continuously sending out updates, we will get this incoming prop
    // far more often... in that case, we only want to clear our undo stack if it is a brand
    // new value.
    // Because it is possible state hasn't synchronized yet, we check against a _pendingValue
    // that we set when sending that value out
    if (!this.props.continuouslyUpdates || value !== this._pendingValue) {
      this.clearUndoStack(value);
    }
    this._pendingValue = null;
    this.setState({ value: value, initialValue: value });
    this._uncommittedChange = false;
  }

  componentDidUpdate() {
    if (this._pendingSelection) {
      this._domNode.setSelectionRange(this._pendingSelection.start, this._pendingSelection.end);
      this._pendingSelection = null;
    }
  }

  componentWillUnmount() {
    // If there are changes to commit, we need to commit them before the component unmounts
    // Otherwise, we could lose the edits on actions like changing view types
    if (this._uncommittedChange) {
      this.commitChange();
    }
    this.stopListeningToSlidePortal();
  }

  stopListeningToSlidePortal() {
    if (this.props.portalChildContext && this.props.portalChildContext.unregisterTransitionEndCallback) {
      this.props.portalChildContext.unregisterTransitionEndCallback(this.onInitialSlidePortalTransitionEnd);
    }
  }

  onInitialSlidePortalTransitionEnd() {
    this.stopListeningToSlidePortal();
    const autoFocus = this.props.autoFocus || (this.props.autoFocusOnEmpty && !this.state.value);
    if (autoFocus) {
      this.focus();
    }
  }

  onFocusIn() {
    this._isFocused = true;
    this._pubSubUndoToken = PubSub.subscribe(PubSubTopics.undoText, this.undoChange);
    this._pubSubRedoToken = PubSub.subscribe(PubSubTopics.redoText, this.redoChange);
    this.clearUndoStack(this.state.value);
    if (!this.props.multiline && this._domNode) {
      this._domNode.select();
    }
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  }

  onFocusOut() {
    this._isFocused = false;
    if (this._pubSubUndoToken) {
      PubSub.unsubscribe(this._pubSubUndoToken);
    }
    if (this._pubSubRedoToken) {
      PubSub.unsubscribe(this._pubSubRedoToken);
    }
    this._pubSubUndoToken = null;
    this._pubSubRedoToken = null;

    // And finally, clear our undo stack so we are not holding onto anything for next time!
    this.clearUndoStack(this.state.value);
  }

  getSelectionRange() {
    return localUndoUtils.makeSelection(this._domNode.selectionStart, this._domNode.selectionEnd);
  }

  // Handling our own local Undo
  clearUndoStack(value) {
    this._localUndoStack = localUndoUtils.reset(value);
    this.debouncedUpdateUndoStack.cancel();
  }

  updateUndoStack() {
    if (this.state.value !== this._localUndoStack.present) {
      this._localUndoStack = localUndoUtils.change(this._localUndoStack, this.state.value, this.getSelectionRange());
    }
  }

  updateUndoSelection() {
    this._localUndoStack = localUndoUtils.moveSelection(
      this._localUndoStack,
      this.state.value,
      this.getSelectionRange()
    );
  }

  undoChange() {
    this._localUndoStack = localUndoUtils.undo(this._localUndoStack);
    this.applyPresentMoment();
  }

  redoChange() {
    this._localUndoStack = localUndoUtils.redo(this._localUndoStack);
    this.applyPresentMoment();
  }

  applyPresentMoment() {
    const moment = this._localUndoStack.present;
    const value = moment.value;
    if (value != this.state.value) {
      this._pendingSelection = moment.selection;
      this.setState({
        value
      });
      if (this.props.continuouslyUpdates) {
        this.onSendChangeToProps(value);
      }
    }
    this.debouncedUpdateUndoStack.cancel();
  }

  onSendChangeToProps(value) {
    this._pendingValue = value;
    this.props.onChange(value);
  }

  // TODO: Add validation of element before allowing change...?
  // TODO: Add specific validation for type=number
  onChange(e) {
    const v = e.target.value;
    const value = v;
    this.setState({ value });
    this._uncommittedChange = true;
    if (this.props.continuouslyUpdates) {
      this.onSendChangeToProps(value);
    }
    this.debouncedUpdateUndoStack();
  }

  onKeyDown(e) {
    const modifiers = getModifierKeysForEvent(e);

    // Before we do anything else, we will update our selection range with the current selection
    // That means that if the user starts typing, we will have the most accurate historic version
    // to go back to.
    this.updateUndoSelection();

    switch (e.key) {
      // Tab will lose focus so commit the change
      case KeyConstants.Tab: {
        this.commitChange();
        break;
      }
      // If we don't allow new lines or new lines must be escaped with Alt and this one
      // is not, then commit the change upon Enter instead of adding the new line character.
      // If specified, blur the field also
      // Inside a table view, enter will move to the next line down unless escaped (mustEscapeNewLine will be set to true)
      case KeyConstants.Enter: {
        if (!this.props.allowNewLine || (this.props.mustEscapeNewLine && !modifiers.ALT && !modifiers.SHIFT)) {
          // ENTER is a commit action
          if (!this.props.allowEnterEvent) {
            e.preventDefault();
          }
          if (this.state.value !== this.state.initialValue) {
            this.commitChange();
          }
          if (!this.props.noBlurOnCommit && this._domNode) {
            this._domNode.blur();
          }
        } else {
          // ENTER is a new line character
          e.stopPropagation();
        }
        if (this.props.onEnter) {
          this.props.onEnter();
        }
        break;
      }
      case KeyConstants.Down: {
        if (this.props.onDownArrow) {
          this.props.onDownArrow();
        }
        e.stopPropagation();
        break;
      }
      case KeyConstants.Up:
      case KeyConstants.Left:
      case KeyConstants.Right:
        e.stopPropagation();
        break;
    }
  }

  onKeyPress(e) {
    const modifiers = getModifierKeysForEvent(e);

    // Only setup on numeric inputs
    // Block anything that is not a valid numeric character
    // We will allow digits, dot and comma
    if (this.props.numeric && !modifiers.COMMAND && !modifiers.CONTROL) {
      const digit = Number(e.key);
      const isPeriod = e.key === KeyConstants.Period;
      // const isComma = e.key === KeyConstants.Comma;
      const isMinus = e.key === KeyConstants.Minus;
      if (isNaN(digit) && (!isPeriod || this.props.isInteger) && !isMinus) {
        e.preventDefault();
      }
    }
  }

  focus() {
    if (this._domNode && !this._isFocused) {
      this._domNode.focus();
      this._domNode.select();
    }
  }

  onBlur(_e) {
    this.onFocusOut();
    this.commitChange();
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  }

  validate() {
    const { value } = this.state;
    const convertedValue = this.props.numeric ? Number(value) : value;
    if (this.props.validator && !this.props.validator(convertedValue)) {
      return false;
    }
    if (this.props.numeric) {
      if (!isNaN(convertedValue)) {
        if (this.props.isInteger && !isInteger(convertedValue)) {
          return false;
        }
        if (!isNil(this.props.max) && convertedValue > this.props.max) {
          return false;
        }
        if (!isNil(this.props.min) && convertedValue < this.props.min) {
          return false;
        }
      } else {
        return false;
      }
    }
    return true;
  }

  commitChange() {
    const { value, initialValue } = this.state;
    if (this._uncommittedChange && value !== initialValue) {
      if (this.validate()) {
        const sendValue = this.props.numeric ? Number(value) : trimValueBeforeCommit(value);
        this.onSendChangeToProps(sendValue);
      } else {
        this.setState({
          value: initialValue
        });
      }
    }
    this._uncommittedChange = false;
  }

  getAutoExpandSize() {
    let styleObj = this.props.style || {};
    if (this._domNode) {
      const totalPadding = 2 * Dimensions.INSPECTOR_TEXT_AREA_PADDING;
      const width = this._domNode.getBoundingClientRect().width - totalPadding;
      const { size } = calcTextSize(
        this.state.value,
        this.props.characterContext.getFontSize(),
        this.props.characterContext.getWidthMap(),
        width,
        true
      );
      styleObj["height"] = size.height;
    }
    return styleObj;
  }

  getStyle() {
    const { style, getSizeFunction, multiline, autoExpandHeight } = this.props;

    let styleObj = style || {};
    if (getSizeFunction) {
      const size = getSizeFunction(
        !this.state.value && this.props.placeholder ? this.props.placeholder : this.state.value
      );
      if (size) {
        if (size.width) {
          styleObj["width"] = size.width;
        }
        if (size.height) {
          styleObj["height"] = size.height;
        }
      }
    } else if (multiline && autoExpandHeight) {
      styleObj = this.getAutoExpandSize();
    }
    return styleObj;
  }

  render() {
    const classes = [this.props.isStruckOut ? "isStruckOut" : ""].join(" ");
    if (this.props.multiline) {
      return (
        <textarea
          className={classes}
          style={this.getStyle()}
          ref={input => {
            this._domNode = input;
          }}
          disabled={this.props.disabled}
          onFocus={this.onFocusIn}
          onBlur={this.props.disabled ? this.onFocusOut : this.onBlur}
          onChange={this.props.disabled ? null : this.onChange}
          value={this.state.value}
          placeholder={this.props.placeholder}
          onKeyDown={this.props.disabled ? null : this.onKeyDown}
          autoComplete={this.props.disableAutoComplete ? "off" : null}
          spellCheck={this.props.disableSpellCheck ? "false" : null}
        />
      );
    } else if (this.props.numeric) {
      return (
        <input
          className={classes}
          style={this.getStyle()}
          type="text"
          ref={input => {
            this._domNode = input;
          }}
          disabled={this.props.disabled}
          onFocus={this.onFocusIn}
          onBlur={this.props.disabled ? this.onFocusOut : this.onBlur}
          onChange={this.props.disabled ? null : this.onChange}
          onKeyDown={this.props.disabled ? null : this.onKeyDown}
          onKeyPress={this.props.disabled ? null : this.onKeyPress}
          value={this.state.value}
          placeholder={this.props.placeholder}
          min={this.props.min ? this.props.min : null}
          max={this.props.max ? this.props.max : null}
          autoComplete={"off"}
          spellCheck={"false"}
        />
      );
    } else if (this.props.email) {
      return (
        <input
          className={classes}
          style={this.getStyle()}
          type="email"
          ref={input => {
            this._domNode = input;
          }}
          disabled={this.props.disabled}
          onFocus={this.onFocusIn}
          onBlur={this.props.disabled ? this.onFocusOut : this.onBlur}
          onChange={this.props.disabled ? null : this.onChange}
          onKeyDown={this.props.disabled ? null : this.onKeyDown}
          value={this.state.value}
          placeholder={this.props.placeholder}
        />
      );
    } else if (this.props.isUrlField) {
      return (
        <input
          className={classes}
          style={this.getStyle()}
          type="url"
          ref={input => {
            this._domNode = input;
          }}
          disabled={this.props.disabled}
          onFocus={this.onFocusIn}
          onBlur={this.props.disabled ? this.onFocusOut : this.onBlur}
          onChange={this.props.disabled ? null : this.onChange}
          onKeyDown={this.props.disabled ? null : this.onKeyDown}
          autoComplete={"off"}
          spellCheck={"false"}
          value={this.state.value}
          placeholder={this.props.placeholder}
        />
      );
    } else {
      return (
        <input
          className={classes}
          style={this.getStyle()}
          type="text"
          ref={input => {
            this._domNode = input;
          }}
          disabled={this.props.disabled}
          onFocus={this.onFocusIn}
          onBlur={this.props.disabled ? this.onFocusOut : this.onBlur}
          onChange={this.props.disabled ? null : this.onChange}
          onKeyDown={this.props.disabled ? null : this.onKeyDown}
          value={this.state.value}
          placeholder={this.props.placeholder}
          autoComplete={this.props.disableAutoComplete ? "off" : null}
          spellCheck={this.props.disableSpellCheck ? "false" : null}
        />
      );
    }
  }
}

FormTextField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  multiline: PropTypes.bool,
  allowNewLine: PropTypes.bool,
  mustEscapeNewLine: PropTypes.bool,
  // Particularly for using in table view
  noBlurOnCommit: PropTypes.bool,
  // For the component has its own enter event handler
  // Eg. FormDatePicker
  allowEnterEvent: PropTypes.bool,
  numeric: PropTypes.bool,
  email: PropTypes.bool,
  isInteger: PropTypes.bool,
  isUrlField: PropTypes.bool,
  autoFocusOnEmpty: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disableAutoComplete: PropTypes.bool,
  disableSpellCheck: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  onDownArrow: PropTypes.func,
  // Set this to true will ignore the validator and numeric check
  continuouslyUpdates: PropTypes.bool,
  validate: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  style: PropTypes.object,
  getSizeFunction: PropTypes.func,
  autoExpandHeight: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  isStruckOut: PropTypes.bool,
  // Value validator
  // if input value cannot pass, should reset to the initial value
  validator: PropTypes.func,

  // From withPortalChildContext
  portalChildContext: PropTypes.object,

  // From character context
  characterContext: PropTypes.object.isRequired
};

let Component = withCharacterContext(FormTextField);
//#!if iOS === true
// Component = withPortalChildContext(Component);
//#!endif

export default Component;
